<template>
  <div class="space-y-6">
    <!-- Party Selection Card -->
    <AppCard
      title="Select Parties"
      subtitle="Choose which parties to include as participants"
    >
      <template #actions>
        <div class="flex gap-1 text-xs">
          <button
            class="cursor-pointer font-medium text-green-600 hover:text-green-700"
            @click="selectAll"
          >
            Select all
          </button>
          <span class="text-neutral-300">&middot;</span>
          <button
            class="text-navy-400 hover:text-navy-600 cursor-pointer font-medium"
            @click="deselectAll"
          >
            Deselect all
          </button>
        </div>
      </template>

      <div class="space-y-2">
        <label
          v-for="(party, index) in parties"
          :key="index"
          :class="[
            'flex cursor-pointer items-center gap-3.5 rounded-lg border-[1.5px] p-3.5 transition-all',
            isSelected(index)
              ? 'border-green-200 bg-green-50'
              : 'border-neutral-200 bg-white',
          ]"
        >
          <input
            type="checkbox"
            :checked="isSelected(index)"
            class="sr-only"
            @change="$emit('toggleParty', index)"
          />
          <!-- Checkbox indicator -->
          <div
            :class="[
              'flex h-5 w-5 shrink-0 items-center justify-center rounded border-[1.5px] transition-all',
              isSelected(index)
                ? 'border-green-500 bg-green-500'
                : 'border-neutral-300 bg-white',
            ]"
          >
            <svg
              v-if="isSelected(index)"
              class="h-3 w-3 text-white"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              viewBox="0 0 12 12"
            >
              <path d="M2.5 6l2.5 2.5 4.5-5" />
            </svg>
          </div>

          <!-- Party info -->
          <div class="min-w-0 flex-1">
            <p class="text-navy-800 text-sm font-semibold">
              {{ party.FullName }}
            </p>
            <p
              v-if="party.Email || party.Phone"
              class="text-navy-400 mt-0.5 truncate text-xs"
            >
              {{ [party.Email, party.Phone].filter(Boolean).join(" \u00B7 ") }}
            </p>
          </div>

          <!-- Role badge -->
          <StatusBadge
            :variant="roleBadge(party.Role).variant"
            :label="roleBadge(party.Role).label"
          />
        </label>
      </div>
    </AppCard>

    <!-- Identity Verification Card -->
    <div>
      <label
        :class="[
          'flex cursor-pointer items-center gap-3.5 rounded-[10px] border-[1.5px] p-4 transition-all',
          verificationEnabled
            ? 'border-green-500 bg-green-50'
            : 'border-navy-100 bg-neutral-50',
        ]"
      >
        <div
          :class="[
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border transition-all',
            verificationEnabled
              ? 'border-green-500 bg-green-100'
              : 'border-navy-100 bg-white',
          ]"
        >
          <svg
            :class="[
              'h-[18px] w-[18px] transition-colors',
              verificationEnabled ? 'text-green-700' : 'text-navy-400',
            ]"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            viewBox="0 0 18 18"
          >
            <path
              d="M9 1.5L2.5 4.5v4c0 4.5 3 7.5 6.5 9 3.5-1.5 6.5-4.5 6.5-9v-4L9 1.5z"
            />
            <path d="M6.5 9l2 2 3.5-4" />
          </svg>
        </div>
        <div class="flex-1">
          <p class="text-navy-800 text-[13.5px] font-semibold">
            ID Verification
          </p>
          <p class="text-navy-400 mt-0.5 text-xs">
            Require participants to verify identity with a photo ID and selfie
          </p>
        </div>
        <div class="relative h-6 w-11 shrink-0">
          <input
            :checked="verificationEnabled"
            type="checkbox"
            class="peer sr-only"
            @change="$emit('toggleVerification', !verificationEnabled)"
          />
          <div
            class="bg-navy-200 h-6 w-11 rounded-full transition-colors peer-checked:bg-green-600"
          />
          <div
            class="absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5"
          />
        </div>
      </label>
    </div>

    <!-- Document Email Card -->
    <div>
      <label
        :class="[
          'flex cursor-pointer items-center gap-3.5 rounded-[10px] border-[1.5px] p-4 transition-all',
          documentEmailEnabled
            ? 'border-green-500 bg-green-50'
            : 'border-navy-100 bg-neutral-50',
        ]"
      >
        <div
          :class="[
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border transition-all',
            documentEmailEnabled
              ? 'border-green-500 bg-green-100'
              : 'border-navy-100 bg-white',
          ]"
        >
          <svg
            :class="[
              'h-[18px] w-[18px] transition-colors',
              documentEmailEnabled ? 'text-green-700' : 'text-navy-400',
            ]"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            viewBox="0 0 18 18"
          >
            <rect x="1.5" y="4" width="15" height="11" rx="1.5" />
            <path d="M1.5 6l7.5 5 7.5-5" />
          </svg>
        </div>
        <div class="flex-1">
          <p class="text-navy-800 text-[13.5px] font-semibold">
            Document Email
          </p>
          <p class="text-navy-400 mt-0.5 text-xs">
            Email closing documents to participants after signing
          </p>
        </div>
        <div class="relative h-6 w-11 shrink-0">
          <input
            :checked="documentEmailEnabled"
            type="checkbox"
            class="peer sr-only"
            @change="$emit('toggleDocumentEmail', !documentEmailEnabled)"
          />
          <div
            class="bg-navy-200 h-6 w-11 rounded-full transition-colors peer-checked:bg-green-600"
          />
          <div
            class="absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5"
          />
        </div>
      </label>
    </div>

    <!-- Navigation footer -->
    <div class="flex items-center justify-between">
      <button
        class="text-navy-500 hover:text-navy-700 cursor-pointer text-sm transition-colors"
        @click="$emit('back')"
      >
        &larr; Back to file number
      </button>
      <div class="flex items-center gap-4">
        <AppButton @click="$emit('continue')">
          Continue to Documents
        </AppButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { FetchedOrderParty } from "../../pages/Order/types"
import AppButton from "../ui/AppButton.vue"
import AppCard from "../ui/AppCard.vue"
import StatusBadge from "../ui/StatusBadge.vue"

const roleMap: Record<
  FetchedOrderParty["Role"],
  { variant: "blue" | "purple" | "info" | "neutral"; label: string }
> = {
  Buyer: { variant: "blue", label: "Buyer" },
  Seller: { variant: "purple", label: "Seller" },
  SettlementAgent: { variant: "info", label: "Settlement Agent" },
  ListingAgentBroker: { variant: "neutral", label: "Listing Agent" },
  SellingAgentBroker: { variant: "neutral", label: "Selling Agent" },
  Lender: { variant: "info", label: "Lender" },
}

const roleBadge = (role: FetchedOrderParty["Role"]) =>
  roleMap[role] ?? { variant: "neutral" as const, label: role }

const props = defineProps<{
  parties: FetchedOrderParty[]
  selectedIndices: number[]
  verificationEnabled: boolean
  documentEmailEnabled: boolean
}>()

const emit = defineEmits<{
  toggleParty: [index: number]
  toggleVerification: [enabled: boolean]
  toggleDocumentEmail: [enabled: boolean]
  back: []
  continue: []
}>()

const isSelected = (index: number) => props.selectedIndices.includes(index)

const selectAll = () => {
  props.parties.forEach((_, index) => {
    if (!isSelected(index)) {
      emit("toggleParty", index)
    }
  })
}

const deselectAll = () => {
  props.parties.forEach((_, index) => {
    if (isSelected(index)) {
      emit("toggleParty", index)
    }
  })
}
</script>
